import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { createUploadLink } from 'apollo-upload-client';
import { ApolloClient, InMemoryCache, concat, DefaultOptions, ApolloLink } from '@apollo/client';
import { v4 as uuidv4 } from 'uuid';

import { typePolicies } from 'utils/apolloHelpers';
import { show } from 'store/slices/modal';
import { dispatch } from 'store';

let mockLink: ApolloLink | null = null;

const uploadLink = createUploadLink({
    uri: process.env.REACT_APP_GRAPHQL_URL,
    credentials: 'include'
});

// Field names that require Cognito token (before backend cookie is set)
const PRE_LOGIN_FIELDS = ['getUserDetails', 'SaasUserLogin'];

// Helper to get the first field name from a GraphQL operation
const getFieldName = (operation: {
    query: { definitions: Array<{ selectionSet?: { selections: Array<{ name?: { value: string } }> } }> };
}) => {
    try {
        const firstDefinition = operation.query.definitions[0];
        if (firstDefinition?.selectionSet?.selections?.[0]?.name?.value) {
            return firstDefinition.selectionSet.selections[0].name.value;
        }
    } catch {
        return null;
    }
    return null;
};

const authLink = setContext((operation, { headers }) => {
    const tenant_id = localStorage.getItem('tenant_id') || undefined;

    // Generate unique nonce and timestamp for each request
    const nonce = uuidv4();
    const timestamp = Date.now().toString();

    // Only send Cognito token for pre-login operations (before backend_jwt cookie exists)
    // All other operations rely on the backend_jwt cookie sent via credentials: 'include'
    const fieldName = getFieldName(operation as unknown as Parameters<typeof getFieldName>[0]);
    const needsAuthHeader = fieldName ? PRE_LOGIN_FIELDS.includes(fieldName) : false;
    const authorization = needsAuthHeader ? localStorage.getItem('serviceToken') : undefined;

    return {
        headers: {
            ...headers,
            tenant_id,
            ...(authorization && { authorization }),
            'X-Request-Nonce': nonce,
            'X-Request-Timestamp': timestamp
        }
    };
});

const logoutLink = onError(({ graphQLErrors }) => {
    if (graphQLErrors?.some((error) => error.message.includes('TokenExpiredError') || error.extensions.code === 'UNAUTHENTICATED')) {
        dispatch(show({}));
    }
});

const defaultOptions: DefaultOptions = {
    query: {
        fetchPolicy: 'no-cache',
        errorPolicy: 'ignore'
    }
};

const createLink = () => {
    const baseLink = concat(authLink.concat(logoutLink), uploadLink as unknown as ApolloLink);

    if (mockLink) {
        return ApolloLink.from([mockLink, baseLink]);
    }

    return baseLink;
};

export const client = new ApolloClient({
    link: createLink(),
    cache: new InMemoryCache({
        addTypename: false,
        typePolicies
    }),
    defaultOptions
});

export const setMockLink = (link: ApolloLink | null) => {
    mockLink = link;
    client.setLink(createLink());
};
